import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { clientsApi, projectsApi } from '@/lib/api'
import { LANGUAGE_NAMES } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { useNavigate, Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ChevronLeft, FileUp, Upload, ArrowLeftRight, Calendar, Paperclip, X, Loader2, FileText, CheckCircle2 } from 'lucide-react'
import { Progress } from '@/components/ui/progress'

// 分片上传常量：单文件上限 500 MB（与后端 MAX_UPLOAD_FILE_SIZE 对齐）
const MAX_FILE_SIZE = 500 * 1024 * 1024

interface Client {
  id: string
  name: string
  code: string
}

export function ProjectCreate() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [referenceFiles, setReferenceFiles] = useState<File[]>([])
  const [clientId, setClientId] = useState<string>('')
  const [documentType, setDocumentType] = useState<string>('marketing')
  const [sourceLanguage, setSourceLanguage] = useState<string>('en')
  const [targetLanguage, setTargetLanguage] = useState<string>('zh')
  const [deadlineDate, setDeadlineDate] = useState<string>('')
  const [deadlineHour, setDeadlineHour] = useState<string>('')
  const [deadlineMinute, setDeadlineMinute] = useState<string>('')
  // 术语预提取默认开启：有利于全文术语一致性，PM 在项目详情页审核
  const [extractTermsBeforeTranslation] = useState<boolean>(true)
  // 字幕专用：输出模式与上方语言（仅视频字幕类型时展示）
  const [subtitleOutputMode, setSubtitleOutputMode] = useState<'single' | 'bilingual'>('single')
  const [subtitleTopLanguage, setSubtitleTopLanguage] = useState<'source' | 'target'>('target')
  // 短剧专用：任务类型 full=剧本全文 | dialogue=仅翻译台词（仅短剧类型时展示）
  const [dramaTaskType, setDramaTaskType] = useState<'full' | 'dialogue'>('full')
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  // 上传阶段：idle 空闲 / uploading 分片上传中 / parsing 服务器解析中
  const [uploadStage, setUploadStage] = useState<'idle' | 'uploading' | 'parsing'>('idle')
  // 已上传完成的分片数 / 总分片数（用于 UI 显示）
  const [uploadedParts, setUploadedParts] = useState(0)
  const [totalParts, setTotalParts] = useState(0)

  const isSubtitleProject = documentType === 'subtitle'
  const isDramaProject = documentType === 'drama'
  
  const handleSourceLanguageChange = (value: string) => {
    setSourceLanguage(value)
    if (value === 'zh') {
      setTargetLanguage('en')
    } else {
      setTargetLanguage('zh')
    }
  }

  const swapLanguages = () => {
    const temp = sourceLanguage
    setSourceLanguage(targetLanguage)
    setTargetLanguage(temp)
  }

  const { data: clientsResponse } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.getList()
  })
  
  const clients = (clientsResponse?.data || []) as Client[]

  const createProject = useMutation({
    mutationFn: async () => {
      if (!file || !clientId) {
        throw new Error('请选择客户并上传文件')
      }
      if (file.size > MAX_FILE_SIZE) {
        throw new Error(`文件超过 500MB 上限（当前 ${(file.size / 1024 / 1024).toFixed(2)}MB），请拆分后上传或联系管理员`)
      }

      setUploadStage('uploading')
      setUploadProgress(0)
      setUploadedParts(0)
      setTotalParts(0)

      // 1. 初始化分片上传（同时做客户校验 + 60 秒重复检查）
      const initResp = await projectsApi.uploadInit({
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type || 'application/octet-stream',
        clientId,
      })
      if (!initResp.success || !initResp.data) {
        throw new Error(initResp.error || '初始化上传失败')
      }

      // 命中 60 秒重复检查，直接返回原项目 ID
      if (initResp.data.isDuplicate && initResp.data.projectId) {
        return { projectId: initResp.data.projectId, isDuplicate: true }
      }

      const { uploadId, fileKey, partSize, totalParts: tp } = initResp.data
      setTotalParts(tp)

      // 2. 顺序上传分片
      const parts: { partNumber: number; etag: string }[] = []
      try {
        for (let i = 0; i < tp; i++) {
          const start = i * partSize
          const end = Math.min(start + partSize, file.size)
          const chunk = file.slice(start, end)

          const partResp = await projectsApi.uploadPart(
            { uploadId, fileKey, partNumber: i + 1, chunk },
            (progress) => {
              // 整体进度 = (已完成片数 + 当前片进度/100) / 总片数 × 99
              // 预留 1% 给 complete 阶段，避免显示 100% 后仍在转
              const overall = ((i + progress / 100) / tp) * 99
              setUploadProgress(Math.round(overall))
            },
          )
          if (!partResp.success || !partResp.data) {
            throw new Error(partResp.error || `第 ${i + 1} 片上传失败`)
          }
          parts.push({ partNumber: i + 1, etag: partResp.data.etag })
          setUploadedParts(i + 1)
        }
      } catch (error: any) {
        // 分片失败：尝试 abort 清理 R2 中的 incomplete multipart upload（避免占存储）
        try {
          await projectsApi.uploadAbort({ uploadId, fileKey })
        } catch (abortErr) {
          console.error('[Upload] Abort failed:', abortErr)
        }
        throw error
      }

      // 3. 切到解析阶段：合并分片 + 调 Python Engine 解析 + 建项目
      setUploadStage('parsing')
      setUploadProgress(99)

      const completeResp = await projectsApi.uploadComplete({
        uploadId,
        fileKey,
        parts,
        fileName: file.name,
        clientId,
        documentType,
        sourceLanguage,
        targetLanguage,
        deadline: (deadlineDate && deadlineHour && deadlineMinute)
          ? new Date(`${deadlineDate}T${deadlineHour}:${deadlineMinute}`).toISOString()
          : undefined,
        extractTermsBeforeTranslation,
        subtitleOutputMode,
        subtitleTopLanguage,
        dramaTaskType,
        referenceFiles,
      })
      if (!completeResp.success || !completeResp.data) {
        throw new Error(completeResp.error || '完成上传失败')
      }

      setUploadProgress(100)
      return completeResp.data
    },
    onSuccess: (data: any) => {
      setUploadProgress(null)
      setUploadStage('idle')
      setUploadedParts(0)
      setTotalParts(0)
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      if (data.isDuplicate) {
        toast.info('检测到 60 秒内已创建过同名项目，已跳转至原项目')
      } else {
        toast.success('项目创建成功，已开始解析文件')
      }
      navigate({ to: `/projects/${data.projectId}` })
    },
    onError: (error: any) => {
      setUploadProgress(null)
      setUploadStage('idle')
      setUploadedParts(0)
      setTotalParts(0)
      const message = error.error || error.message || '未知错误'
      toast.error(`创建失败: ${message}`)
    }
  })

  const isUploading = createProject.isPending && uploadStage === 'uploading'
  const isParsing = createProject.isPending && uploadStage === 'parsing'

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!file || !clientId) {
      toast.error('请选择客户并上传文件')
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`文件超过 500MB 上限（当前 ${(file.size / 1024 / 1024).toFixed(2)}MB），请拆分后上传或联系管理员`)
      return
    }

    // 分片上传：mutationFn 内部从组件 state 取所有信息（file/clientId/documentType 等）
    createProject.mutate()
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/projects">
            <ChevronLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">新建翻译项目</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>上传待译文件</CardTitle>
          <CardDescription>
            {isSubtitleProject
              ? '支持 .srt 字幕格式。文件将自动解析为待译段落。'
              : '支持 .docx, .pdf, .xlsx 等格式。文件将自动解析为待译段落。'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="client">选择客户</Label>
              <Select onValueChange={setClientId} value={clientId}>
                <SelectTrigger>
                  <SelectValue placeholder="请选择客户" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name} ({client.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="documentType">文档类型</Label>
              <Select onValueChange={setDocumentType} value={documentType}>
                <SelectTrigger>
                  <SelectValue placeholder="请选择文档类型" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">媒体稿件</SelectItem>
                  <SelectItem value="subtitle">视频字幕</SelectItem>
                  <SelectItem value="marketing">市场文案</SelectItem>
                  <SelectItem value="drama">短剧剧本</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isSubtitleProject && (
              <div className="space-y-3 rounded-lg border border-warning/20 bg-warning/5 p-4">
                <div className="space-y-2">
                  <Label htmlFor="subtitleOutputMode">字幕输出模式</Label>
                  <Select onValueChange={(v) => setSubtitleOutputMode(v as 'single' | 'bilingual')} value={subtitleOutputMode}>
                    <SelectTrigger>
                      <SelectValue placeholder="请选择输出模式" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">单语字幕（仅译文）</SelectItem>
                      <SelectItem value="bilingual">双语字幕（原文+译文）</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    单语：导出仅含译文；双语：每条字幕原文与译文分上下两行显示。
                  </p>
                </div>

                {subtitleOutputMode === 'bilingual' && (
                  <div className="space-y-2">
                    <Label htmlFor="subtitleTopLanguage">上方语言</Label>
                    <Select onValueChange={(v) => setSubtitleTopLanguage(v as 'source' | 'target')} value={subtitleTopLanguage}>
                      <SelectTrigger>
                        <SelectValue placeholder="请选择上方语言" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="target">{LANGUAGE_NAMES[targetLanguage] || targetLanguage}（译文在上）</SelectItem>
                        <SelectItem value="source">{LANGUAGE_NAMES[sourceLanguage] || sourceLanguage}（原文在上）</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      决定双语字幕中哪一行显示在上方。
                    </p>
                  </div>
                )}
              </div>
            )}

            {isDramaProject && (
              <div className="space-y-3 rounded-lg border border-warning/20 bg-warning/5 p-4">
                <div className="space-y-2">
                  <Label htmlFor="dramaTaskType">短剧任务类型</Label>
                  <Select onValueChange={(v) => setDramaTaskType(v as 'full' | 'dialogue')} value={dramaTaskType}>
                    <SelectTrigger>
                      <SelectValue placeholder="请选择短剧任务类型" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">剧本全文（翻译整本剧本）</SelectItem>
                      <SelectItem value="dialogue">仅台词（只翻译居中台词，其余原样保留）</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    剧本全文：翻译场景标题、动作描写、人物名、台词、括号提示等所有内容。
                    仅台词：只翻译居中段落（人物名/台词/括号动作），非居中段落（场景标题、动作描写）作为参考、原样保留。
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-2">
                <Label htmlFor="sourceLanguage">源语言</Label>
                <Select onValueChange={handleSourceLanguageChange} value={sourceLanguage}>
                  <SelectTrigger>
                    <SelectValue placeholder="源语言" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(LANGUAGE_NAMES).map(([code, name]) => (
                      <SelectItem key={code} value={code}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="pb-1">
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="icon" 
                  className="h-10 w-10 rounded-full hover:bg-muted"
                  onClick={swapLanguages}
                  title="互换语言"
                >
                  <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>

              <div className="flex-1 space-y-2">
                <Label htmlFor="targetLanguage">目标语言</Label>
                <Select onValueChange={setTargetLanguage} value={targetLanguage}>
                  <SelectTrigger>
                    <SelectValue placeholder="目标语言" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(LANGUAGE_NAMES).map(([code, name]) => (
                      <SelectItem key={code} value={code}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="deadline" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                截止日期（可选）
              </Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  id="deadline"
                  value={deadlineDate}
                  onChange={(e) => setDeadlineDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  className="flex-1 cursor-pointer"
                />
                <Select value={deadlineHour} onValueChange={setDeadlineHour}>
                  <SelectTrigger className="w-[88px]">
                    <SelectValue placeholder="时" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map((h) => (
                      <SelectItem key={h} value={h}>{h}时</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={deadlineMinute} onValueChange={setDeadlineMinute}>
                  <SelectTrigger className="w-[88px]">
                    <SelectValue placeholder="分" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="00">00分</SelectItem>
                    <SelectItem value="15">15分</SelectItem>
                    <SelectItem value="30">30分</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                设置截止日期后，系统将在截止前2小时发送邮件提醒
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="file">源文件</Label>
              <input
                type="file"
                id="file"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              {file ? (
                <div
                  className={`rounded-lg border-2 transition-colors ${isUploading ? 'border-primary/40 bg-primary/5' : isParsing ? 'border-warning/30 bg-warning/10' : 'border-primary/30 bg-primary/5'}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault()
                    if (!createProject.isPending) {
                      const droppedFile = e.dataTransfer.files[0]
                      if (droppedFile) setFile(droppedFile)
                    }
                  }}
                >
                  <div className="flex items-center gap-3 p-4">
                    <div className="shrink-0">
                      {isParsing ? (
                        <Loader2 className="h-8 w-8 animate-spin text-warning" />
                      ) : isUploading ? (
                        <FileUp className="h-8 w-8 text-primary" />
                      ) : (
                        <CheckCircle2 className="h-8 w-8 text-success" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm truncate">{file.name}</span>
                        {!createProject.isPending && (
                          <label htmlFor="file" className="cursor-pointer text-xs text-primary hover:underline shrink-0">
                            更换
                          </label>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                        {file.size > MAX_FILE_SIZE && (
                          <span className="text-destructive"> · 超过 500MB 上限</span>
                        )}
                        {isUploading && totalParts > 0 && (
                          <> · 上传中 {uploadProgress}% <span className="text-muted-foreground/70">（{uploadedParts}/{totalParts} 片）</span></>
                        )}
                        {isUploading && totalParts === 0 && ' · 准备上传...'}
                        {isParsing && ' · 服务器解析中（大文件可能需 1-2 分钟）...'}
                        {!createProject.isPending && file.size <= MAX_FILE_SIZE && ' · 已就绪'}
                      </div>
                      {(isUploading || isParsing) && (
                        <Progress value={uploadProgress || 0} className="h-1.5" />
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <label
                  htmlFor="file"
                  className="flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-10 text-center transition-colors cursor-pointer hover:border-primary/50 hover:bg-muted/50"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault()
                    const droppedFile = e.dataTransfer.files[0]
                    if (droppedFile) setFile(droppedFile)
                  }}
                >
                  <div className="flex items-center justify-center h-12 w-12 rounded-full bg-muted">
                    <Upload className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <span className="font-medium text-sm">点击或拖拽上传源文件</span>
                  <span className="text-xs text-muted-foreground">
                    {isSubtitleProject
                      ? '支持 .srt 字幕格式'
                      : '支持 .docx / .pdf / .xlsx / .pptx / .idml 等格式'}
                  </span>
                  <span className="text-xs text-muted-foreground/70">单文件最大 500MB，超过请拆分</span>
                </label>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="referenceFiles" className="flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                参考文件（可选）
              </Label>
              <input
                type="file"
                id="referenceFiles"
                multiple
                className="hidden"
                onChange={(e) => {
                  const picked = Array.from(e.target.files || [])
                  if (picked.length > 0) setReferenceFiles((prev) => [...prev, ...picked])
                  e.target.value = ''
                }}
              />
              {referenceFiles.length > 0 ? (
                <div className="space-y-2">
                  <div className={`rounded-lg border-2 transition-colors ${isUploading ? 'border-primary/40 bg-primary/5' : 'border-primary/20 bg-primary/5'}`}>
                    <div className="flex items-center justify-between px-4 pt-3 pb-1">
                      <span className="text-xs font-medium text-muted-foreground">
                        {referenceFiles.length} 个参考文件
                        {isUploading && ' · 上传中'}
                        {isParsing && ' · 后台解析中'}
                      </span>
                      {!createProject.isPending && (
                        <label htmlFor="referenceFiles" className="cursor-pointer text-xs text-primary hover:underline">
                          + 添加
                        </label>
                      )}
                    </div>
                    <div className="divide-y">
                      {referenceFiles.map((refFile, idx) => (
                        <div key={`${refFile.name}-${idx}`} className="flex items-center gap-3 px-4 py-2.5">
                          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                            <span className="truncate text-sm">{refFile.name}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-xs text-muted-foreground">
                                {(refFile.size / 1024 / 1024).toFixed(2)} MB
                              </span>
                              {!createProject.isPending && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5"
                                  onClick={() => setReferenceFiles((prev) => prev.filter((_, i) => i !== idx))}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {isUploading && (
                      <div className="px-4 pb-3">
                        <Progress value={uploadProgress || 0} className="h-1.5" />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <label
                  htmlFor="referenceFiles"
                  className="flex flex-col items-center justify-center gap-1.5 border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer hover:border-primary/50 hover:bg-muted/50"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault()
                    const dropped = Array.from(e.dataTransfer.files)
                    if (dropped.length > 0) setReferenceFiles((prev) => [...prev, ...dropped])
                  }}
                >
                  <div className="flex items-center justify-center h-10 w-10 rounded-full bg-muted">
                    <Upload className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <span className="font-medium text-sm">点击或拖拽上传参考文件</span>
                  <span className="text-xs text-muted-foreground">支持双语/单语参考文件，可多选</span>
                </label>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={createProject.isPending || (file !== null && file.size > MAX_FILE_SIZE)}
            >
              {createProject.isPending
                ? (isUploading ? `上传中 ${uploadProgress}%` : '服务器解析中...')
                : (file && file.size > MAX_FILE_SIZE ? '文件超过 500MB 上限' : '开始翻译')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
